// CF-ANALYTICS-FOUNDATION-V1 Ph-2: generic per-event client beacon batcher (frontend).
//
// Generalises the CF-DEMO-TELEMETRY-V1 pure-primitive pattern (demo-telemetry.ts) into a typed
// AnalyticsEvent batcher. It is a Plausible-FREE, identity-FREE, batched beacon: the backend
// derives (auth_bot, tg_user_id) from the signed initData (apiFetch attaches X-Telegram-Init-Data)
// and maps it to an OPAQUE subject_id server-side — this module NEVER puts tg_user_id / username /
// wallet / raw initData in an event. The request body is exactly { events: [ ...identity-free... ] }.
//
// Design constraints (design §5 catalog + §6 trust boundary):
//  - source='client' for every event → never trusted by the server for money/labels.
//  - Batched: flush only after a small count/size threshold, and on visibilitychange/pagehide.
//  - Snapshot-then-reset AT dispatch. No indefinite retry — prefer losing telemetry over
//    double-counting (a failed flush's events are already gone, by design).
//  - Never throw into game settlement: dispatch is fire-and-forget (the caller's sender swallows
//    rejections). The pure functions below never throw on normal input.
//  - Variant-agnostic: carries no brand copy — the same batcher serves the primary / dragontiger /
//    german builds (events key on subject_id + auth_bot, resolved server-side).
//
// The accumulator + decision logic are pure and unit-tested (root jest spec). The DOM wiring
// (visibility/pagehide listeners, the real apiFetch sender) lives in the component shell
// (CoinFlip.tsx / App.tsx), which composes these primitives — exactly like demo-telemetry.ts.

/** A single client analytics event. Identity is NEVER a field here — it rides the auth envelope. */
export interface AnalyticsEvent {
  /** Client-assigned UUID — transport dedup (analytics_events PK). */
  event_id: string;
  /** Event time, unix MILLISECONDS UTC (device clock). */
  ts_ms: number;
  /** Per-app-open session id (groups a player's events WITHOUT identity). */
  session_id: string;
  /** Monotonic per-session counter — part of the server-side demo dedup_key basis. */
  client_seq: number;
  /** Allowlist-enforced event type (server rejects anything off the allowlist). */
  event_type: string;
  /** 'real' | 'demo'. The beacon path is demo + lifecycle; real money is server-mirrored, not here. */
  mode?: 'real' | 'demo';
  // Promoted typed columns — only demo_flip_settled sets these (the ML grain).
  bet_nano?: string;
  side?: number;
  tier?: string;
  multiplier_bps?: number;
  outcome?: number;
  won?: number;
  payout_nano?: string;
  balance_after_nano?: string;
  /** Long-tail JSON properties — a plain object, NEVER carries identity. */
  properties?: Record<string, unknown>;
}

/** The fields a caller supplies for a new event; the batcher stamps event_id / ts_ms / client_seq. */
export type AnalyticsEventInput = Omit<AnalyticsEvent, 'event_id' | 'ts_ms' | 'client_seq'>;

/** Immutable accumulator state: the pending events + the monotonic per-session sequence cursor. */
export interface EventBatchState {
  /** Events accumulated since the last dispatch (immutable — every op returns a new array). */
  pending: ReadonlyArray<AnalyticsEvent>;
  /** Next client_seq to assign (monotonic for the life of the app-open). */
  nextSeq: number;
}

/** The beacon body posted to /api/v1/events. ONLY an events array — no identity, no top-level meta. */
export interface EventsBeacon {
  events: AnalyticsEvent[];
}

/** Flush once this many events have accumulated (batched — not per-event). */
export const EVENTS_FLUSH_COUNT_THRESHOLD = 10;
/** Flush once the serialised beacon would exceed this many bytes (keeps a single POST bounded). */
export const EVENTS_FLUSH_BYTES_THRESHOLD = 24 * 1024; // 24 KB — comfortably under the ≤100-event server cap
/** Hard cap mirrored from the server (MAX_EVENTS_PER_BEACON); the batcher flushes well below this. */
export const MAX_EVENTS_PER_BEACON = 100;

export function emptyEventBatchState(): EventBatchState {
  return { pending: [], nextSeq: 0 };
}

/**
 * Generate a new session id for one app-open. A v4 UUID via the platform crypto (browser + Node 18+
 * both expose `globalThis.crypto.randomUUID`); falls back to a timestamp+random token if absent so a
 * session is always groupable. Pure-ish (reads only the crypto global) and never throws.
 */
export function newSessionId(): string {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch {
    /* fall through to the fallback token */
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Generate a new event_id (v4 UUID, same crypto path as newSessionId; fallback token if absent). */
function newEventId(): string {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch {
    /* fall through */
  }
  return `e_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Append one event to the batch (immutable update). The batcher stamps `event_id` (uuid),
 * `ts_ms` (now, injectable for tests), and the monotonic `client_seq`. Returns a NEW state.
 * Never throws — a malformed `properties` is the server's problem (it quarantines), not ours.
 *
 * @param nowMs injectable clock for deterministic tests; defaults to Date.now().
 */
export function appendEvent(
  state: EventBatchState,
  input: AnalyticsEventInput,
  nowMs: number = Date.now(),
): EventBatchState {
  const event: AnalyticsEvent = {
    ...input,
    event_id: newEventId(),
    ts_ms: Number.isFinite(nowMs) ? Math.floor(nowMs) : Date.now(),
    client_seq: state.nextSeq,
  };
  return {
    pending: [...state.pending, event],
    nextSeq: state.nextSeq + 1,
  };
}

/**
 * Threshold flush decision for the count/size-driven path. True once the pending count hits the
 * count threshold OR the serialised beacon would exceed the byte threshold (or the hard event cap
 * is reached). Used after each appendEvent. (The visibility/pagehide path flushes unconditionally
 * via shouldFlushOnHide.)
 */
export function shouldFlushByThreshold(state: EventBatchState): boolean {
  if (state.pending.length >= EVENTS_FLUSH_COUNT_THRESHOLD) return true;
  if (state.pending.length >= MAX_EVENTS_PER_BEACON) return true;
  if (approxBeaconBytes(state) >= EVENTS_FLUSH_BYTES_THRESHOLD) return true;
  return false;
}

/** Anything pending is worth flushing when the app is being backgrounded/closed. */
export function shouldFlushOnHide(state: EventBatchState): boolean {
  return state.pending.length > 0;
}

/** Cheap upper-bound on the serialised beacon size (the JSON of the pending events). */
function approxBeaconBytes(state: EventBatchState): number {
  if (state.pending.length === 0) return 0;
  try {
    return JSON.stringify(state.pending).length;
  } catch {
    // A non-serialisable event slipped in — treat as "large" so we flush (and the server drops it).
    return EVENTS_FLUSH_BYTES_THRESHOLD;
  }
}

/**
 * Build the beacon body from the pending state. ONLY { events: [...] } — no identity, no top-level
 * metadata. Returns null when there is nothing to send (so the caller skips the network call).
 * Caps the batch at MAX_EVENTS_PER_BEACON (the caller should flush before hitting it; this is a
 * defensive slice so an over-full batch never gets rejected wholesale by the server's 400).
 */
export function buildEventsBeacon(state: EventBatchState): EventsBeacon | null {
  if (state.pending.length === 0) return null;
  const events = state.pending.slice(0, MAX_EVENTS_PER_BEACON).map((e) => ({ ...e }));
  return { events };
}
